import { api } from '@api'
import { useQuery } from '@tanstack/react-query'
import { secureTokenStore } from '@store'

/**
 * Authoritative subscription status for the current user.
 * Backed by the Phase 0 API contract: `GET /me/subscription`.
 */
export interface Subscription {
  isPremium: boolean
  expiresAt: string | null
  store: string | null
}

const DEFAULT_SUBSCRIPTION: Subscription = {
  isPremium: false,
  expiresAt: null,
  store: null,
}

// Premium status changes rarely (a purchase / a renewal), so we avoid
// refetching it on every screen focus. Five minutes is a reasonable window.
const SUBSCRIPTION_STALE_TIME_MS = 5 * 60 * 1000

export const SUBSCRIPTION_QUERY_KEY = ['subscription', 'me'] as const

const fetchSubscription = async (): Promise<Subscription> => {
  const { data } = await api.get<Subscription>('/me/subscription')
  return {
    isPremium: Boolean(data?.isPremium),
    expiresAt: data?.expiresAt ?? null,
    store: data?.store ?? null,
  }
}

/**
 * Full subscription query. Runs only when the user is authenticated
 * (guarded on the access token, like other authed reads in the app).
 */
export const useSubscription = () => {
  const isAuthenticated = !!secureTokenStore.getAccessToken()

  const query = useQuery<Subscription>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: fetchSubscription,
    enabled: isAuthenticated,
    staleTime: SUBSCRIPTION_STALE_TIME_MS,
  })

  const subscription = query.data ?? DEFAULT_SUBSCRIPTION

  return {
    isPremium: subscription.isPremium,
    expiresAt: subscription.expiresAt,
    store: subscription.store,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

/**
 * Convenience wrapper — the single source of truth for "is this user Premium?".
 * Use this for feature-gating (Phase 2) and paywall UI.
 */
export const usePremium = (): { isPremium: boolean; isLoading: boolean } => {
  const { isPremium, isLoading } = useSubscription()
  return { isPremium, isLoading }
}
